import { Injectable } from '@angular/core';
import { WindowReferenceService } from 'src/app/shared/services/window-reference.service';
import {
  DATA_LAYER_AFFILIATION,
  DATA_LAYER_EVENTS,
  DATA_LAYER_INTERACTION_TYPES,
  DATA_LAYER_PROCESS_TYPES,
  DATA_LAYER_PRODUCT_VARIANTS,
  DATA_LAYER_SIGNUP_TYPES,
} from '../constants/data-layer.constants';
import {
  DataLayerAddToCart,
  DataLayerItemField,
  DataLayerPurchase,
  DataLayerPushData,
  DataLayerReturnFluSubmit,
  DataLayerReturnFormStart,
  DataLayerUserLogin,
  DataLayerUserRegistration,
  DataLayerViewCart,
  DataLayerViewProduct,
} from '../models/data-layer.model';

@Injectable({
  providedIn: 'root',
})
export class DataLayerService {
  constructor(protected windowRef: WindowReferenceService) {}

  private pushData(obj: DataLayerPushData) {
    this.windowRef.nativeWindow.dataLayer.push(obj);
  }

  addToCart(params: DataLayerAddToCart): void {
    this.pushData({
      event: DATA_LAYER_EVENTS.ADD_TO_CART,
      interaction_type: DATA_LAYER_INTERACTION_TYPES.ECOMMERCE,
      process_type: DATA_LAYER_PROCESS_TYPES.STANDARD_PROCESS,
      location_id: params.userName,
      multiple_locations: 'no',
      locations_number: 1,
      ecommerce: {
        items: [this.itemMapper(params)],
      },
    });
  }

  viewCart(params: DataLayerViewCart): void {
    this.pushData({
      event: DATA_LAYER_EVENTS.VIEW_CART,
      interaction_type: DATA_LAYER_INTERACTION_TYPES.ECOMMERCE,
      process_type: params.processType,
      location_id: params?.shipToParty?.uid,
      multiple_locations: params.childCartsNumber > 1 ? 'yes' : 'no',
      locations_number: params.childCartsNumber,
      ecommerce: {
        value: params?.totalPrice?.value,
        items: this.entriesMapper(params.entries),
      },
    });
  }

  beginCheckout(params: DataLayerViewCart): void {
    this.pushData({
      event: DATA_LAYER_EVENTS.BEGIN_CHECKOUT,
      interaction_type: DATA_LAYER_INTERACTION_TYPES.ECOMMERCE,
      process_type: params.processType,
      location_id: params?.shipToParty?.uid,
      multiple_locations: params.childCartsNumber > 1 ? 'yes' : 'no',
      locations_number: params.childCartsNumber,
      ecommerce: {
        value: params?.totalPrice?.value,
        items: this.entriesMapper(params.entries),
      },
    });
  }

  purchase(params: DataLayerPurchase): void {
    this.pushData({
      event: DATA_LAYER_EVENTS.PURCHASE,
      interaction_type: DATA_LAYER_INTERACTION_TYPES.ECOMMERCE,
      process_type: params.processType,
      location_id: params?.shipToLoc_id,
      ecommerce: {
        transaction_id: params.code,
        transaction_type: 'standard',
        affiliation: DATA_LAYER_AFFILIATION,
        value: params?.totalPrice?.value,
        tax: params?.totalTax?.value,
        shipping: params?.deliveryCost?.value,
        discount: params?.totalDiscounts?.value,
        items: this.entriesMapper(params?.entries),
      },
    });
  }

  returnFluSubmit(params: DataLayerReturnFluSubmit): void {
    this.pushData({
      event: DATA_LAYER_EVENTS.RETURN_FLU_CREDIT_SUBMIT,
      interaction_type: DATA_LAYER_INTERACTION_TYPES.ECOMMERCE,
      account_id: params.userId,
      items: params.items.map((item) => ({
        item_id: item.productCode,
        item_name: item.productName,
        quantity: item.returnedQty,
      })),
    });
  }

  returnFluStart(params: DataLayerReturnFormStart) {
    this.pushData({
      event: DATA_LAYER_EVENTS.RETURN_FLU_CREDIT_START,
      interaction_type: DATA_LAYER_INTERACTION_TYPES.ECOMMERCE,
      account_id: params.userId,
    });
  }

  viewProduct(params: DataLayerViewProduct) {
    this.pushData({
      event: DATA_LAYER_EVENTS.VIEW_ITEM,
      interaction_type: DATA_LAYER_INTERACTION_TYPES.ECOMMERCE,
      ecommerce: {
        items: [this.itemMapper(params)],
      },
    });
  }

  userRegistration(params: DataLayerUserRegistration) {
    this.pushData({
      event: DATA_LAYER_EVENTS.USER_REGISTRATION,
      interaction_type: DATA_LAYER_INTERACTION_TYPES.AUTHENTICATION,
      sign_up_type: DATA_LAYER_SIGNUP_TYPES.NEW_USER,
      user_id: params.userId,
    });
  }

  userLogin(params: DataLayerUserLogin) {
    this.pushData({
      event: DATA_LAYER_EVENTS.USER_LOGIN,
      interaction_type: DATA_LAYER_INTERACTION_TYPES.AUTHENTICATION,
      user_id: params.userId,
    });
  }

  entriesMapper(entries): DataLayerItemField[] {
    return entries?.map((entry) => this.itemMapper(entry)) || [];
  }

  itemMapper(item): DataLayerItemField {
    return {
      item_id: item.code || item.product?.code,
      item_name: item.name || item.product?.name,
      affiliation: DATA_LAYER_AFFILIATION,
      currency: item.price?.currencyIso || item.totalPrice?.currencyIso,
      discount: item.discount,
      index: item.entryNumber,
      item_brand: item.brand,
      item_category:
        item.categories?.[0]?.code || item.product?.categories?.[0]?.code,
      item_category2:
        item.categories?.[1]?.code || item.product?.categories?.[1]?.code,
      item_variant: this.variantMapper(item),
      price: item.price?.value || item.totalPrice?.value,
      quantity: item.quantity,
    };
  }

  variantMapper(item): string {
    if (item.quantityPerUnit || item.product?.quantityPerUnit) {
      const doses = item.quantityPerUnit || item.product?.quantityPerUnit;
      return `${doses} ${DATA_LAYER_PRODUCT_VARIANTS.DOSES}`;
    }
    return item.variant || item.product?.variant;
  }
}
